import * as React from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';

import { Grid, Skeleton } from '@mui/material';
import { Formik, Field } from 'formik';

import { useApi } from '../shared/context';
import {
	CerebroHeading,
	CerebroText,
	CerebroPrimaryButton,
	CerebroSelectableIcon,
	CerebroPageContainer,
	CerebroBreadcrumb,
	CerebroForm,
} from '../shared/components';

import { ReactComponent as CashAndMarketables } from '../shared/icons/CashAndMarketables.svg';
import { ReactComponent as AccountsReceivables } from '../shared/icons/AccountsReceivable.svg';
import { ReactComponent as Inventory } from '../shared/icons/Inventory.svg';
import { ReactComponent as FurnitureFixtures } from '../shared/icons/FurnitureFixtures.svg';
import { ReactComponent as MachineryEquipment } from '../shared/icons/MachineryEquipment.svg';
import { ReactComponent as RollingStockVehicles } from '../shared/icons/RollingStockVehicles.svg';
import { ReactComponent as MarineVessels } from '../shared/icons/MarineVessels.svg';
import { ReactComponent as Helicopters } from '../shared/icons/Helicopters.svg';
import { ReactComponent as FixedAircraft } from '../shared/icons/FixedWingAircraft.svg';
import { ReactComponent as OwnerOccupiedRealEstate } from '../shared/icons/OwnerOccupiedRealEstate.svg';

import { getApiResponseData } from '../shared/utils/axios';
import { useSnackbar } from 'notistack';
import { DealRequestAllocation } from '../shared/models/DealRequestAllocation';
import { FormikUtils } from '../shared/utils';
import { QueryKeys, useDealRequestAllocationQuery, useNavigateToNextPage } from '../shared/hooks';
import { CerebroApiEndpoints } from '../shared/utils/api';

export const BorrowerCollateralTypes: React.FC = () => {
	const api = useApi();
	const { guid } = useParams<'guid'>();
	const navigateToNextPage = useNavigateToNextPage();

	const allocationQuery = useDealRequestAllocationQuery(guid);

	const client = useQueryClient();
	const snackbar = useSnackbar();

	const allocationMutation = useMutation(
		async ({ values }: DealAllocationMutationVariable) => {
			const { data, error } = await getApiResponseData<DealRequestAllocation>(
				api.patch(CerebroApiEndpoints.Allocation(guid), values)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: data => {
				client.setQueryData(QueryKeys.Allocation(guid), data);
				client.invalidateQueries(QueryKeys.Matching.Get(guid));
				snackbar.enqueueSnackbar('Progress Saved.', { variant: 'success' });
				navigateToNextPage();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroPageContainer>
			<CerebroBreadcrumb>Financials &gt; Borrower &gt; Collateral Types</CerebroBreadcrumb>

			<CerebroHeading sx={{ marginBottom: 3 }}>Any additional assets to document?</CerebroHeading>

			<CerebroText sx={{ marginBottom: 8 }}>Select all that apply.</CerebroText>

			{allocationQuery.isLoading ? (
				<Skeleton height={400} />
			) : (
				<Formik
					initialValues={{
						hasInventory:
							(allocationQuery.data?.hasInventory ?? false) ||
							(allocationQuery.data?.financeExpensesOrGrowth ?? false) ||
							(allocationQuery.data?.purchaseWorkingCapital ?? false),
						hasOwnerOccupiedRealEstate:
							(allocationQuery.data?.hasOwnerOccupiedRealEstate ?? false) ||
							(allocationQuery.data?.purchaseRealEstate ?? false),
						hasFurnitureFixtures: allocationQuery.data?.hasFurnitureFixtures ?? null,
						hasMachineryEquipment:
							(allocationQuery.data?.hasMachineryEquipment ?? false) ||
							(allocationQuery.data?.purchaseEquipment ?? false),
						hasRollingStock: allocationQuery.data?.hasRollingStock ?? null,
						hasMarineVessels: allocationQuery.data?.hasMarineVessels ?? null,
						hasHelicopters: allocationQuery.data?.hasHelicopters ?? null,
						hasFixedWingAircraft: allocationQuery.data?.hasFixedWingAircraft ?? null,
						hasCashMarketables:
							(allocationQuery.data?.hasCashMarketables ?? false) ||
							(allocationQuery.data?.financeExpensesOrGrowth ?? false) ||
							(allocationQuery.data?.purchaseWorkingCapital ?? false),
						hasAccountsReceivables:
							(allocationQuery.data?.hasAccountsReceivables ?? false) ||
							(allocationQuery.data?.financeExpensesOrGrowth ?? false) ||
							(allocationQuery.data?.purchaseWorkingCapital ?? false),
					}}
					onSubmit={FormikUtils.MutateOnSubmit(allocationMutation.mutate)}
				>
					{({ isSubmitting, handleSubmit }) => (
						<CerebroForm onSubmit={handleSubmit}>
							{hasRequiredCollateral(allocationQuery.data) && (
								<>
									<CerebroHeading size="h3">Required Collateral Types</CerebroHeading>
									<br />

									<Grid container spacing={2} sx={{ marginBottom: 4 }}>
										{allocationQuery.data?.purchaseEquipment === true && (
											<Grid item xs={12} md={6} lg={3}>
												<CerebroSelectableIcon
													name="requiredMachineryEquipment"
													sx={{ marginBottom: 2 }}
													stretch
													value={true}
													icon={() => <MachineryEquipment />}
												>
													Machinery &#38; Equipment
												</CerebroSelectableIcon>
											</Grid>
										)}
										{allocationQuery.data?.purchaseRealEstate === true && (
											<Grid item xs={12} md={6} lg={3}>
												<CerebroSelectableIcon
													name="requiredRealEstate"
													sx={{ marginBottom: 2 }}
													stretch
													value={true}
													icon={() => <OwnerOccupiedRealEstate />}
												>
													Owner Occupied Real Estate
												</CerebroSelectableIcon>
											</Grid>
										)}
										{(allocationQuery.data?.financeExpensesOrGrowth === true ||
											allocationQuery.data?.purchaseWorkingCapital === true) && (
											<>
												<Grid item xs={12} md={6} lg={3}>
													<CerebroSelectableIcon
														name="requiredCashMarketables"
														sx={{ marginBottom: 2 }}
														stretch
														value={true}
														icon={() => <CashAndMarketables />}
													>
														Cash &#38; Marketables
													</CerebroSelectableIcon>
												</Grid>
												<Grid item xs={12} md={6} lg={3}>
													<CerebroSelectableIcon
														name="requiredAccountsReceivables"
														sx={{ marginBottom: 2 }}
														stretch
														value={true}
														icon={() => <AccountsReceivables />}
													>
														Accounts Receivable
													</CerebroSelectableIcon>
												</Grid>
												<Grid item xs={12} md={6} lg={3}>
													<CerebroSelectableIcon
														name="requiredInventory"
														sx={{ marginBottom: 2 }}
														stretch
														value={true}
														icon={() => <Inventory />}
													>
														Inventory
													</CerebroSelectableIcon>
												</Grid>
											</>
										)}
									</Grid>
								</>
							)}

							{allocationQuery.data?.financeExpensesOrGrowth === false &&
								allocationQuery.data?.purchaseWorkingCapital === false && (
									<>
										<CerebroHeading size="h3">Balance Sheet</CerebroHeading>

										<br />

										<Grid container spacing={2} sx={{ marginBottom: 4 }}>
											<Grid item xs={12} md={6} lg={3}>
												<Field
													as={CerebroSelectableIcon}
													name="hasCashMarketables"
													sx={{ marginBottom: 2 }}
													stretch
													disabled={isSubmitting}
													icon={() => <CashAndMarketables />}
												>
													Cash &#38; Marketables
												</Field>
											</Grid>
											<Grid item xs={12} md={6} lg={3}>
												<Field
													as={CerebroSelectableIcon}
													name="hasAccountsReceivables"
													sx={{ marginBottom: 2 }}
													stretch
													disabled={isSubmitting}
													icon={() => <AccountsReceivables />}
												>
													Accounts Receivable
												</Field>
											</Grid>
										</Grid>
									</>
								)}

							<CerebroHeading size="h3">Tangible Assets</CerebroHeading>

							<br />

							<Grid container spacing={2} sx={{ marginBottom: 6 }}>
								{allocationQuery.data?.financeExpensesOrGrowth === false &&
									allocationQuery.data?.purchaseWorkingCapital === false && (
										<Grid item xs={12} md={6} lg={3}>
											<Field
												as={CerebroSelectableIcon}
												name="hasInventory"
												sx={{ marginBottom: 2 }}
												stretch
												disabled={isSubmitting}
												icon={() => <Inventory />}
											>
												Inventory
											</Field>
										</Grid>
									)}
								{allocationQuery.data?.purchaseRealEstate === false && (
									<Grid item xs={12} md={6} lg={3}>
										<Field
											as={CerebroSelectableIcon}
											name="hasOwnerOccupiedRealEstate"
											sx={{ marginBottom: 2 }}
											stretch
											disabled={isSubmitting}
											icon={() => <OwnerOccupiedRealEstate />}
										>
											Owner Occupied Real Estate
										</Field>
									</Grid>
								)}
								<Grid item xs={12} md={6} lg={3}>
									<Field
										as={CerebroSelectableIcon}
										name="hasFurnitureFixtures"
										sx={{ marginBottom: 2 }}
										stretch
										disabled={isSubmitting}
										icon={() => <FurnitureFixtures />}
									>
										Furniture / Fixtures
									</Field>
								</Grid>
								{allocationQuery.data?.purchaseEquipment === false && (
									<Grid item xs={12} md={6} lg={3}>
										<Field
											as={CerebroSelectableIcon}
											name="hasMachineryEquipment"
											sx={{ marginBottom: 2 }}
											stretch
											disabled={isSubmitting}
											icon={() => <MachineryEquipment />}
										>
											Machinery &#38; Equipment
										</Field>
									</Grid>
								)}
								<Grid item xs={12} md={6} lg={3}>
									<Field
										as={CerebroSelectableIcon}
										name="hasRollingStock"
										sx={{ marginBottom: 2 }}
										stretch
										disabled={isSubmitting}
										icon={() => <RollingStockVehicles />}
									>
										Rolling Stock / Vehicles
									</Field>
								</Grid>
								<Grid item xs={12} md={6} lg={3}>
									<Field
										as={CerebroSelectableIcon}
										name="hasMarineVessels"
										sx={{ marginBottom: 2 }}
										stretch
										disabled={isSubmitting}
										icon={() => <MarineVessels />}
									>
										Marine Vessels
									</Field>
								</Grid>
								<Grid item xs={12} md={6} lg={3}>
									<Field
										as={CerebroSelectableIcon}
										name="hasHelicopters"
										sx={{ marginBottom: 2 }}
										stretch
										disabled={isSubmitting}
										icon={() => <Helicopters />}
									>
										Helicopters
									</Field>
								</Grid>
								<Grid item xs={12} md={6} lg={3}>
									<Field
										as={CerebroSelectableIcon}
										name="hasFixedWingAircraft"
										sx={{ marginBottom: 2 }}
										stretch
										disabled={isSubmitting}
										icon={() => <FixedAircraft />}
									>
										Fixed Aircraft
									</Field>
								</Grid>
							</Grid>

							<Grid
								container
								sx={{ paddingBottom: 2 }}
								justifyContent={{ xs: 'center', md: 'flex-start' }}
							>
								<Grid item>
									<CerebroPrimaryButton loading={isSubmitting} type="submit">
										Save &amp; Continue
									</CerebroPrimaryButton>
								</Grid>
							</Grid>
						</CerebroForm>
					)}
				</Formik>
			)}
		</CerebroPageContainer>
	);
};

function hasRequiredCollateral(values?: DealRequestAllocation) {
	if (values == null) return false;
	const isAcquisition =
		values.dealType === 'AcquisitionManagementBuyout' || values.dealType === 'AcquisitionPartnerBuyout';
	return (
		!isAcquisition &&
		(values.purchaseEquipment ||
			values.purchaseRealEstate ||
			values.financeExpensesOrGrowth ||
			values.purchaseWorkingCapital)
	);
}

interface BorrowerCollateralTypesFormValues {
	hasInventory: boolean;
	hasOwnerOccupiedRealEstate: boolean;
	hasFurnitureFixtures: boolean | null;
	hasMachineryEquipment: boolean;
	hasRollingStock: boolean | null;
	hasMarineVessels: boolean | null;
	hasHelicopters: boolean | null;
	hasFixedWingAircraft: boolean | null;
	hasCashMarketables: boolean;
	hasAccountsReceivables: boolean;
}

type DealAllocationMutationVariable = FormikUtils.MutateOnSubmitVariables<BorrowerCollateralTypesFormValues>;
